import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

// Default settings schema
const DEFAULT_SETTINGS = {
  version: '1.0',
  lastUpdated: new Date().toISOString(),
  compression: {
    activeProfile: 'balanced',
    customProfile: {
      resolution: { width: 1920, height: 1080 },
      quality: 0.85,
    },
    testResults: {
      lastRun: null,
      results: [],
    },
  },
  general: {
    theme: 'dark',
  },
  network: {
    autoDetect: true,
  },
};

const STORAGE_KEY = 'fpstak_settings';

/**
 * Settings Context Provider
 * 
 * Manages application-wide settings including compression profiles,
 * user preferences, and test results.
 * 
 * Features:
 * - Persistent storage via localStorage
 * - Real-time updates across components
 * - Migration support for future versions
 * - Helper methods for common operations
 */
export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        
        // Validate version and migrate if needed
        if (parsed.version === DEFAULT_SETTINGS.version) {
          setSettings(parsed);
        } else {
          console.log('[Settings] Migrating settings from version', parsed.version, 'to', DEFAULT_SETTINGS.version);
          // Future: Add migration logic here
          setSettings(DEFAULT_SETTINGS);
        }
      }
    } catch (error) {
      console.error('[Settings] Failed to load settings from localStorage:', error);
      // Fall back to defaults
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    if (isLoaded) {
      try {
        const toSave = {
          ...settings,
          lastUpdated: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        console.log('[Settings] Saved to localStorage');
      } catch (error) {
        console.error('[Settings] Failed to save settings to localStorage:', error);
      }
    }
  }, [settings, isLoaded]);

  /**
   * Update entire settings object
   */
  const updateSettings = (newSettings) => {
    setSettings(newSettings);
  };

  /**
   * Update compression settings
   */
  const updateCompression = (compressionSettings) => {
    setSettings((prev) => ({
      ...prev,
      compression: {
        ...prev.compression,
        ...compressionSettings,
      },
    }));
  };

  /**
   * Set active compression profile
   */
  const setActiveProfile = (profileId) => {
    console.log('[Settings] Setting active profile:', profileId);
    setSettings((prev) => ({
      ...prev,
      compression: {
        ...prev.compression,
        activeProfile: profileId,
      },
    }));
  };

  /**
   * Update custom profile settings
   */
  const updateCustomProfile = (customSettings) => {
    console.log('[Settings] Updating custom profile:', customSettings);
    setSettings((prev) => ({
      ...prev,
      compression: {
        ...prev.compression,
        customProfile: {
          ...prev.compression.customProfile,
          ...customSettings,
        },
      },
    }));
  };

  /**
   * Save test results
   */
  const saveTestResults = (results) => {
    console.log('[Settings] Saving test results:', results.length, 'results');
    setSettings((prev) => ({
      ...prev,
      compression: {
        ...prev.compression,
        testResults: {
          lastRun: new Date().toISOString(),
          results,
        },
      },
    }));
  };

  /**
   * Reset settings to defaults
   */
  const resetToDefaults = () => {
    console.log('[Settings] Resetting to defaults');
    setSettings(DEFAULT_SETTINGS);
  };

  /**
   * Get active profile configuration
   * Returns the full profile object based on activeProfile ID
   */
  const getActiveProfile = () => {
    return settings.compression.activeProfile;
  };

  /**
   * Export settings as JSON (for backup)
   */
  const exportSettings = () => {
    return JSON.stringify(settings, null, 2);
  };

  /**
   * Import settings from JSON
   */
  const importSettings = (jsonString) => {
    try {
      const imported = JSON.parse(jsonString);
      setSettings(imported);
      console.log('[Settings] Imported settings successfully');
      return true;
    } catch (error) {
      console.error('[Settings] Failed to import settings:', error);
      return false;
    }
  };

  const value = {
    settings,
    isLoaded,
    updateSettings,
    updateCompression,
    setActiveProfile,
    updateCustomProfile,
    saveTestResults,
    resetToDefaults,
    getActiveProfile,
    exportSettings,
    importSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Hook to use settings context
 */
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;

